/**
 * 传奇作品列表页面
 *
 * 浏览社区所有传奇作品
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LegendCard, LegendCardSkeleton } from '../components/legend';
import * as legendApi from '../services/legendApi';
import styles from './LegendsPage.module.css';

const SORT_OPTIONS = [
    { value: 'hot', label: { zh: '热门', en: 'Hot', de: 'Beliebt' }, icon: '🔥' },
    { value: 'new', label: { zh: '最新', en: 'New', de: 'Neu' }, icon: '✨' },
    { value: 'rising', label: { zh: '上升', en: 'Rising', de: 'Aufsteigend' }, icon: '📈' },
    { value: 'featured', label: { zh: '精选', en: 'Featured', de: 'Empfohlen' }, icon: '⭐' },
];

const STYLE_OPTIONS = [
    { value: 'all', label: { zh: '全部', en: 'All', de: 'Alle' } },
    { value: 'classic', label: { zh: '经典', en: 'Classic', de: 'Klassisch' } },
    { value: 'custom', label: { zh: '改装', en: 'Custom', de: 'Custom' } },
    { value: 'racing', label: { zh: '竞速', en: 'Racing', de: 'Rennen' } },
    { value: 'touring', label: { zh: '旅行', en: 'Touring', de: 'Touring' } },
];

export default function LegendsPage() {
    const { language } = useLanguage();

    const [legends, setLegends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sort, setSort] = useState('hot');
    const [style, setStyle] = useState('all');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const loadLegends = useCallback(async (reset = false) => {
        if (reset) {
            setLoading(true);
            setPage(1);
        } else {
            setLoadingMore(true);
        }

        try {
            const currentPage = reset ? 1 : page;
            const result = await legendApi.getLegends({
                sort,
                style,
                page: currentPage,
                limit: 12
            });

            if (result.success) {
                const newLegends = result.data || [];
                if (reset) {
                    setLegends(newLegends);
                } else {
                    setLegends(prev => [...prev, ...newLegends]);
                }
                setHasMore(newLegends.length === 12);
                if (!reset) {
                    setPage(prev => prev + 1);
                }
            }
        } catch (err) {
            console.error('Failed to load legends:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [sort, style, page]);

    useEffect(() => {
        loadLegends(true);
    }, [sort, style]);

    const handleLoadMore = () => {
        if (!loadingMore && hasMore) {
            setPage(prev => prev + 1);
            loadLegends(false);
        }
    };

    const getLabel = (option) => {
        return option.label[language] || option.label.en;
    };

    return (
        <div className={styles.page}>
            {/* 页面头部 */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>传奇画廊</h1>
                    <p className={styles.subtitle}>探索社区骑士们的精彩创作</p>
                </div>
            </header>

            {/* 筛选栏 */}
            <div className={styles.filters}>
                <div className={styles.filtersContent}>
                    {/* 排序 */}
                    <div className={styles.sortTabs}>
                        {SORT_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                className={`${styles.sortTab} ${sort === option.value ? styles.active : ''}`}
                                onClick={() => setSort(option.value)}
                            >
                                <span className={styles.sortIcon}>{option.icon}</span>
                                {getLabel(option)}
                            </button>
                        ))}
                    </div>

                    {/* 风格筛选 */}
                    <div className={styles.styleFilter}>
                        <select
                            value={style}
                            onChange={(e) => setStyle(e.target.value)}
                            className={styles.styleSelect}
                        >
                            {STYLE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {getLabel(option)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* 传奇列表 */}
            <main className={styles.main}>
                {loading ? (
                    <div className={styles.grid}>
                        {[...Array(12)].map((_, i) => (
                            <LegendCardSkeleton key={i} />
                        ))}
                    </div>
                ) : legends.length === 0 ? (
                    <div className={styles.empty}>
                        <span className={styles.emptyIcon}></span>
                        <h3>暂无传奇作品</h3>
                        <p>成为第一个铸造传奇的骑士</p>
                        <Link to="/" className={styles.emptyBtn}>
                            开始创作
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className={styles.grid}>
                            {legends.map(legend => (
                                <LegendCard
                                    key={legend.id}
                                    legend={legend}
                                />
                            ))}
                        </div>

                        {/* 加载更多 */}
                        {hasMore && (
                            <div className={styles.loadMore}>
                                <button
                                    className={styles.loadMoreBtn}
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? '加载中...' : '加载更多'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
